// src/pages/Admin/components/TeamEditor.jsx
// Drift Team Website 2025
//
// Created by Lukas Helebrandt on 17.03.2025.
// Updated by Grok on 18.04.2025 to fix image path handling, improve FormData, and add debug logging
// Fixed by Claude on 18.04.2025 - opravené zobrazení formuláře

import { useState, useEffect } from 'react';
import { get, post, put, del } from '../../utils/api';
import { API_BASE_URL, IMAGE_BASE_URL } from '../../config';
import { getUserLanguage } from '../../utils/language';

const TeamEditor = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentMember, setCurrentMember] = useState({
    id: null,
    image_path: '',
    instagram_url: '',
    facebook_url: '',
    display_order: 0,
    is_active: true,
    translations: {
      en: { name: '', position: '', description: '' },
      cs: { name: '', position: '', description: '' },
    },
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const language = getUserLanguage();

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[TeamEditor] Fetching team members');
      const data = await get('/team-members');
      const processedData = (data || []).map(member => ({
        ...member,
        is_active: !!member.is_active,
        display_order: member.display_order || 0,
        image_path: member.image_path ? member.image_path : '',
        translations: member.translations || {
          en: { name: '', position: '', description: '' },
          cs: { name: '', position: '', description: '' },
        },
      }));
      setTeamMembers(processedData.sort((a, b) => a.display_order - b.display_order));
      console.log('[TeamEditor] Fetched team members:', processedData.length);
      
      // Debug image paths
      processedData.forEach(member => {
        console.log(`[TeamEditor] Member ID ${member.id} image path:`, member.image_path);
      });
    } catch (err) {
      console.error('[TeamEditor] Error fetching team members:', err);
      setError(language === 'cs' ? 'Nepodařilo se načíst členy týmu' : 'Failed to load team members');
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e, lang = null) => {
    try {
      const { name, value, type, checked } = e.target;
      const translationLang = lang || language;
      
      console.log('[TeamEditor] Handling change:', { name, value, lang: translationLang, type, checked });
      
      if (['name', 'position', 'description'].includes(name)) {
        setCurrentMember(prev => ({
          ...prev,
          translations: {
            ...prev.translations,
            [translationLang]: {
              ...prev.translations[translationLang],
              [name]: value,
            },
          },
        }));
      } else {
        setCurrentMember(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value,
        }));
      }
    } catch (err) {
      console.error('[TeamEditor] Error in handleChange:', err);
      setError('Chyba při změně hodnoty formuláře');
    }
  };

  const handleImageChange = (e) => {
    try {
      if (e.target.files && e.target.files.length > 0) {
        setSelectedImage(e.target.files[0]);
        console.log('[TeamEditor] Selected image:', e.target.files[0].name);
      }
    } catch (err) {
      console.error('[TeamEditor] Error in handleImageChange:', err);
      setError('Chyba při výběru obrázku');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      console.log('[TeamEditor] Submitting form:', { editMode, currentMember, selectedImage });

      if (!currentMember.translations?.en?.name || !currentMember.translations?.en?.position ||
          !currentMember.translations?.cs?.name || !currentMember.translations?.cs?.position) {
        setError(language === 'cs' ? 'Jméno a pozice jsou povinné v obou jazycích' : 'Name and position are required in both languages');
        console.error('[TeamEditor] Validation failed: Name and position required');
        return;
      }

      if (!editMode && !selectedImage) {
        setError(language === 'cs' ? 'Fotografie je povinná pro nového člena' : 'Photo is required for new member');
        console.error('[TeamEditor] Validation failed: Image required for new member');
        return;
      }

      const formData = new FormData();
      if (selectedImage) {
        formData.append('image', selectedImage);
        console.log('[TeamEditor] Appending new image:', selectedImage.name);
      } else if (editMode && currentMember.image_path) {
        // Use the image path as-is from the database
        formData.append('image_path', currentMember.image_path);
        console.log('[TeamEditor] Appending existing image_path:', currentMember.image_path);
      }
      formData.append('instagram_url', currentMember.instagram_url || '');
      formData.append('facebook_url', currentMember.facebook_url || '');
      formData.append('display_order', currentMember.display_order || 0);
      formData.append('is_active', currentMember.is_active ? 'true' : 'false');
      formData.append('translations', JSON.stringify(currentMember.translations));

      let response;
      if (editMode) {
        console.log('[TeamEditor] Updating team member ID:', currentMember.id);
        response = await put(`/team-members/${currentMember.id}`, formData);
        setSuccess(language === 'cs' ? 'Člen týmu úspěšně aktualizován' : 'Team member updated successfully');
      } else {
        console.log('[TeamEditor] Creating new team member');
        response = await post('/team-members', formData);
        setSuccess(language === 'cs' ? 'Člen týmu úspěšně přidán' : 'Team member added successfully');
      }

      console.log('[TeamEditor] Submit response:', response);
      resetForm();
      fetchTeamMembers();
    } catch (err) {
      console.error('[TeamEditor] Error saving team member:', err);
      setError(language === 'cs' ? `Nepodařilo se uložit člena týmu: ${err.message}` : `Failed to save team member: ${err.message}`);
    }
  };

  const handleEdit = async (id) => {
    console.log('[TeamEditor] Editing team member ID:', id);
    try {
      const member = await get(`/team-members/${id}`);
      console.log('[TeamEditor] Retrieved member data for edit:', member);
      
      if (!member || !member.id) {
        throw new Error('Nepodařilo se načíst data člena týmu');
      }
      
      // Zajistíme, že translations existuje a má správnou strukturu
      const translations = member.translations || {};
      const en = translations.en || { name: '', position: '', description: '' };
      const cs = translations.cs || { name: '', position: '', description: '' };
      
      setCurrentMember({
        id: member.id,
        image_path: member.image_path || '',
        instagram_url: member.instagram_url || '',
        facebook_url: member.facebook_url || '',
        display_order: parseInt(member.display_order) || 0,
        is_active: !!member.is_active,
        translations: {
          en,
          cs
        },
      });
      setEditMode(true);
      setSelectedImage(null);
      setShowForm(true);
      console.log('[TeamEditor] Set currentMember:', {
        id: member.id,
        image_path: member.image_path,
        translations: {
          en,
          cs
        }
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('[TeamEditor] Error fetching member for edit:', err);
      setError(language === 'cs' ? 'Nepodařilo se načíst člena týmu pro úpravu' : 'Failed to load team member for editing');
    }
  };

  const handleDelete = async (id) => {
    console.log('[TeamEditor] Deleting team member ID:', id);
    if (!window.confirm(language === 'cs' ? 'Opravdu chcete smazat tohoto člena týmu?' : 'Are you sure you want to delete this team member?')) {
      return;
    }
    try {
      await del(`/team-members/${id}`);
      setSuccess(language === 'cs' ? 'Člen týmu byl úspěšně smazán' : 'Team member deleted successfully');
      fetchTeamMembers();
      console.log('[TeamEditor] Team member deleted successfully');
    } catch (err) {
      console.error('[TeamEditor] Error deleting team member:', err);
      setError(language === 'cs' ? 'Nepodařilo se smazat člena týmu' : 'Failed to delete team member');
    }
  };

  const resetForm = () => {
    console.log('[TeamEditor] Resetting form');
    try {
      setCurrentMember({
        id: null,
        image_path: '',
        instagram_url: '',
        facebook_url: '',
        display_order: 0,
        is_active: true,
        translations: {  // Opraveno z стрелы na translations
          en: { name: '', position: '', description: '' },
          cs: { name: '', position: '', description: '' },
        },
      });
      setSelectedImage(null);
      setEditMode(false);
      setShowForm(false);
      setError(null);
      setSuccess(null);
    } catch (err) {
      console.error('[TeamEditor] Error in resetForm:', err);
    }
  };

  const handleAddNew = () => {
    console.log('[TeamEditor] Opening form for new member');
    try {
      resetForm();
      // Ujistíme se, že currentMember je správně nastaven před zobrazením formuláře
      setCurrentMember({
        id: null,
        image_path: '',
        instagram_url: '',
        facebook_url: '',
        display_order: 0,
        is_active: true,
        translations: {
          en: { name: '', position: '', description: '' },
          cs: { name: '', position: '', description: '' },
        },
      });
      setShowForm(true);
    } catch (err) {
      console.error('[TeamEditor] Error in handleAddNew:', err);
      setError('Chyba při otevírání formuláře');
    }
  };

  if (loading) {
    return <div className="admin-p-4">{language === 'cs' ? 'Načítání...' : 'Loading...'}</div>;
  }

  // Pojistka proti chybějícím překladům
  const safeCurrentMember = {
    ...currentMember,
    translations: {
      en: currentMember.translations?.en || { name: '', position: '', description: '' },
      cs: currentMember.translations?.cs || { name: '', position: '', description: '' }
    }
  };

  return (
    <div className="admin-p-4">
      <div className="admin-flex admin-justify-between admin-items-center admin-mb-6">
        <h1 className="admin-text-2xl admin-font-bold">{language === 'cs' ? 'Správa členů týmu' : 'Team Members Management'}</h1>
        <button
          onClick={handleAddNew}
          className="admin-btn admin-btn-primary"
        >
          {language === 'cs' ? 'Přidat člena' : 'Add Member'}
        </button>
      </div>

      {error && (
        <div className="admin-alert admin-alert-error">{error}</div>
      )}
      {success && (
        <div className="admin-alert admin-alert-success">{success}</div>
      )}

      {showForm && (
        <div className="admin-card admin-mb-8 admin-bg-dark-gray">
          <div className="admin-card-header admin-bg-accent-gray">
            <h2 className="admin-text-xl admin-font-semibold">
              {editMode ? (language === 'cs' ? 'Upravit člena týmu' : 'Edit Team Member') : (language === 'cs' ? 'Přidat člena týmu' : 'Add Team Member')}
            </h2>
          </div>
          <div className="admin-card-body">
            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-2 admin-gap-6">
                <div>
                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">English</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Name</label>
                    <input
                      type="text"
                      name="name"
                      className="admin-form-input"
                      value={safeCurrentMember.translations.en.name || ''}
                      onChange={(e) => handleChange(e, 'en')}
                      required
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Position</label>
                    <input
                      type="text"
                      name="position"
                      className="admin-form-input"
                      value={safeCurrentMember.translations.en.position || ''}
                      onChange={(e) => handleChange(e, 'en')}
                      required
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Description</label>
                    <textarea
                      name="description"
                      className="admin-form-textarea"
                      value={safeCurrentMember.translations.en.description || ''}
                      onChange={(e) => handleChange(e, 'en')}
                      rows="4"
                    />
                  </div>
                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">Čeština</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Jméno</label>
                    <input
                      type="text"
                      name="name"
                      className="admin-form-input"
                      value={safeCurrentMember.translations.cs.name || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                      required
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Pozice</label>
                    <input
                      type="text"
                      name="position"
                      className="admin-form-input"
                      value={safeCurrentMember.translations.cs.position || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                      required
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Popis</label>
                    <textarea
                      name="description"
                      className="admin-form-textarea"
                      value={safeCurrentMember.translations.cs.description || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                      rows="4"
                    />
                  </div>
                </div>
                <div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Fotografie' : 'Photo'}</label>
                    <input
                      type="file"
                      name="image"
                      className="admin-form-input"
                      onChange={handleImageChange}
                      accept="image/*"
                      required={!editMode || !safeCurrentMember.image_path}
                    />
                    {editMode && !selectedImage && safeCurrentMember.image_path && (
                      <div className="admin-mt-2">
                        <p className="admin-text-sm admin-text-gray-300">{language === 'cs' ? 'Aktuální obrázek:' : 'Current image:'}</p>
                        <p className="admin-text-xs admin-text-gray-400">{safeCurrentMember.image_path}</p>
                        <img
                          className="admin-mt-2 admin-h-32 admin-object-cover admin-rounded"
                          src={`${IMAGE_BASE_URL}${safeCurrentMember.image_path}`}
                          alt=""
                          onError={(e) => {
                            console.error('[TeamEditor] Image load error:', safeCurrentMember.image_path);
                            e.target.onerror = null;
                            e.target.src = '/images/team/placeholder.png';
                          }}
                        />
                      </div>
                    )}
                    {selectedImage && (
                      <div className="admin-mt-2">
                        <p className="admin-text-sm admin-text-gray-300">{language === 'cs' ? 'Vybraný obrázek:' : 'Selected image:'} {selectedImage.name}</p>
                        <img
                          src={URL.createObjectURL(selectedImage)}
                          alt="Preview"
                          className="admin-mt-2 admin-h-32 admin-object-cover admin-rounded"
                        />
                      </div>
                    )}
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Instagram URL</label>
                    <input
                      type="url"
                      name="instagram_url"
                      className="admin-form-input"
                      value={safeCurrentMember.instagram_url || ''}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Facebook URL</label>
                    <input
                      type="url"
                      name="facebook_url"
                      className="admin-form-input"
                      value={safeCurrentMember.facebook_url || ''}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Pořadí zobrazení' : 'Display Order'}</label>
                    <input
                      type="number"
                      name="display_order"
                      className="admin-form-input"
                      value={safeCurrentMember.display_order || 0}
                      onChange={handleChange}
                      min="0"
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-flex admin-items-center">
                      <input
                        type="checkbox"
                        name="is_active"
                        className="admin-form-checkbox"
                        checked={!!safeCurrentMember.is_active}
                        onChange={handleChange}
                      />
                      <span className="admin-text-white admin-text-sm admin-font-bold">{language === 'cs' ? 'Aktivní' : 'Active'}</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="admin-mt-6 admin-flex admin-items-center">
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary admin-mr-2"
                >
                  {editMode ? (language === 'cs' ? 'Aktualizovat' : 'Update') : (language === 'cs' ? 'Přidat' : 'Add')}
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={resetForm}
                >
                  {language === 'cs' ? 'Zrušit' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-card admin-bg-dark-gray">
        <div className="admin-card-header admin-bg-accent-gray">{language === 'cs' ? 'Seznam členů týmu' : 'Team Members List'}</div>
        {teamMembers.length === 0 ? (
          <div className="admin-p-4 admin-text-center admin-text-gray-300">{language === 'cs' ? 'Žádní členové týmu nebyli nalezeni.' : 'No team members found.'}</div>
        ) : (
          <div className="admin-overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr className="admin-bg-accent-gray">
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Jméno' : 'Name'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Pozice' : 'Position'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Pořadí' : 'Order'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Aktivní' : 'Active'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Akce' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {teamMembers.map((member) => {
                  // Pojistka proti chybějícím překladům
                  const memberTranslations = member.translations || {};
                  const langTranslation = memberTranslations[language] || {};
                  const enTranslation = memberTranslations.en || {};
                  
                  const memberData = {
                    name: langTranslation.name || enTranslation.name || '',
                    position: langTranslation.position || enTranslation.position || '',
                    description: langTranslation.description || enTranslation.description || ''
                  };
                  
                  return (
                    <tr key={member.id} className="admin-table-row">
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-flex admin-items-center">
                          <div className="admin-flex-shrink-0 admin-h-10 admin-w-10">
                            <img
                              className="admin-h-10 admin-w-10 admin-rounded-full admin-object-cover"
                              src={member.image_path ? `${IMAGE_BASE_URL}${member.image_path}` : '/images/team/placeholder.png'}
                              alt={memberData.name || ''}
                              onError={(e) => {
                                console.error('[TeamEditor] Table image load error:', member.image_path);
                                e.target.onerror = null;
                                e.target.src = '/images/team/placeholder.png';
                              }}
                            />
                          </div>
                          <div className="admin-ml-4">
                            <div className="admin-text-sm admin-font-medium admin-text-white">{memberData.name || ''}</div>
                          </div>
                        </div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-text-sm admin-text-white">{memberData.position || ''}</div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-text-sm admin-text-white">{member.display_order || 0}</div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <span className={`admin-badge ${member.is_active ? 'admin-badge-green' : 'admin-badge-red'}`}>
                          {member.is_active ? (language === 'cs' ? 'Ano' : 'Yes') : (language === 'cs' ? 'Ne' : 'No')}
                        </span>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap admin-text-sm admin-font-medium">
                        <button 
                          type="button"
                          onClick={() => handleEdit(member.id)} 
                          className="admin-text-blue-600 hover:admin-text-blue-900 admin-mr-3"
                        >
                          {language === 'cs' ? 'Upravit' : 'Edit'}
                        </button>
                        <button 
                          type="button"
                          onClick={() => handleDelete(member.id)} 
                          className="admin-text-red-600 hover:admin-text-red-900"
                        >
                          {language === 'cs' ? 'Smazat' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamEditor;